import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter, CardDescription } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useQuery, gql } from '@apollo/client';
import { GetMerchantIdQuery } from '@/gql/graphql';
import { useSetupProgress } from '../tracking-utils';
import { Logo } from '@/components/logos/logo';
import { Copy } from 'lucide-react';
import { AppTitle } from '@/components/app-header/app-header';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';

const GET_MERCHANT_ID = gql`
  query GetMerchantId {
    currentMerchant {
      id
      domain
    }
  }
`;

export function SubDomain() {
  const navigate = useNavigate();
  const { data } = useQuery<GetMerchantIdQuery>(GET_MERCHANT_ID);
  const { markSubDomainComplete } = useSetupProgress();
  const getCustomPixelScript = () => {
    const domain = data?.currentMerchant.domain ?? 'your-shop-domain';

    return `<script async src="https://ctx.${domain}/embedded-script"></script>`.trim();
  };

  const copyToClipboard = async (text: string) => {
    if (data?.currentMerchant.domain) {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied to clipboard!',
        variant: 'default'
      });
    } else {
      toast({
        title: 'Unable to copy script',
        description: 'Shop domain is not available. Please try again later.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div>
      <AppTitle>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink className="text-base" asChild>
                <Link to="../../tracking">Tracking</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-base">Sub Domain</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </AppTitle>
      <div className="mx-auto py-4">
        <div className="flex items-center gap-2">
          <Logo icon="subdomain" />
          <h1 className="text-xl font-semibold text-gray-900">Subdomain</h1>
        </div>
      </div>
      <Card className="rounded-lg grid-cols-12 grid gap-6 p-6">
        <CardHeader className="p-0 sm:col-span-4 col-span-12">
          <h2 className="text-xl font-semibold">{`Now let's get Contextual enabled in your subdomain.`}</h2>
          <CardDescription>
            {`Set up this source to know your customers' entire journey by capturing all events from non-Shopify pages (e.g. third party landing pages, wordpress blogs, etc.)`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8 p-0 sm:col-span-8 col-span-12">
          <div className="space-y-8">
            <div>
              <h3 className="font-semibold">{`1. Paste the script below into the <head> section of your website.`}</h3>
              <div className="flex items-center mt-2">
                <div className="bg-gray-100 p-2 rounded flex justify-between items-center flex-1">
                  <pre className="text-gray-500 pl-2 overflow-x-auto text-xs">
                    {getCustomPixelScript()}
                  </pre>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    void copyToClipboard(getCustomPixelScript());
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold">{`2. That's it, you're done!`}</h3>
            </div>
          </div>
        </CardContent>
        <CardFooter className="col-span-12 p-0 mt-4">
          <Button
            onClick={() => {
              markSubDomainComplete();
              void navigate('/tracking');
            }}
          >
            Mark as Complete
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
